import React from "react";
import { FileProcessingStatus } from "../../utils/enums";
import { motion } from "framer-motion";
import { CheckCircle, FileText, ExternalLink, Calendar, Tag } from "lucide-react";

export default function UploadResponse({ data }) {
  const {
    file_id,
    uploaded_file_name,
    upload_date_time,
    processing_status,
    s3_presigned_urldetails,
  } = data;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, type: "spring" }}
      className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 rounded-2xl p-6 mb-6 shadow-lg"
    >
      {/* Success icon with animation */}
      <div className="absolute top-4 right-4">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <CheckCircle className="w-12 h-12 text-green-600" />
        </motion.div>
      </div>

      {/* Decorative elements */}
      <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-green-200/30 rounded-full blur-3xl"></div>
      <div className="absolute -left-10 top-0 w-32 h-32 bg-emerald-200/30 rounded-full blur-2xl"></div>

      <div className="relative z-10">
        <div className="flex items-start gap-4">
          {/* File icon */}
          <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
              Upload Successful! 
              <span className="text-lg">🎉</span>
            </h3>
            
            <p className="text-sm text-gray-700 mb-3">
              <span className="font-semibold text-gray-900">"{uploaded_file_name}"</span> has been uploaded and is being processed.
            </p>

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div className="flex items-center gap-2 text-sm">
                <Tag className="w-4 h-4 text-green-600" />
                <span className="text-gray-600">ID:</span>
                <code className="font-mono text-xs bg-white px-2 py-1 rounded border border-green-200 text-gray-800">
                  {file_id.slice(0, 12)}...
                </code>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-green-600" />
                <span className="text-gray-600">Uploaded:</span>
                <span className="font-medium text-gray-800">
                  {new Date(upload_date_time).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Status badge */}
            <div className="mt-4 pt-4 border-t border-green-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status:</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold border border-blue-200">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-3 h-3 border-2 border-blue-700 border-t-transparent rounded-full"
                  />
                  {FileProcessingStatus.toLabel(processing_status)}
                </span>
              </div>

              {s3_presigned_urldetails && (
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={s3_presigned_urldetails.presigned_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-all shadow-md hover:shadow-lg"
                >
                  <ExternalLink className="w-4 h-4" />
                  View PDF
                </motion.a>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
